<template>
  <section v-if="showSection" class="px-4 py-12 mx-auto max-w-7xl sm:px-6">
    <h2 class="text-4xl sm:text-5xl font-extrabold text-[#004d6b] mb-6">
      Postagens recentes
    </h2>

    <!-- 1 postagem: card único ocupando a largura toda -->
    <div v-if="statePosts.length === 1" class="grid grid-cols-1">
      <RecentPostCard :post="statePosts[0]" size="big" eager />
    </div>

    <!-- 2 postagens: dois cards grandes lado a lado (2/3 + 1/3) -->
    <div v-else-if="statePosts.length === 2" class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <RecentPostCard :post="statePosts[0]" size="big" eager class="lg:col-span-2" />
      <RecentPostCard :post="statePosts[1]" size="big" class="lg:col-span-1" />
    </div>

    <!-- 3+ postagens: principal maior + duas laterais empilhadas -->
    <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <RecentPostCard :post="mainPost" size="big" eager class="lg:col-span-2" />
      <div class="flex flex-col gap-6">
        <RecentPostCard v-for="(post, idx) in sidePosts" :key="idx" :post="post" size="small" />
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { getPosts } from "@/services/blog";
import placeholderPicture from "@/assets/picture-placeholder.svg";
import RecentPostCard from "./RecentPostCard.vue";

const props = defineProps({
  posts: {
    type: Array,
    default: () => [],
  },
});

// Estado interno — a section só existe quando há postagens publicadas de verdade.
const statePosts = ref([...props.posts]);
const showSection = ref(statePosts.value.length > 0);

// Normaliza URL da imagem do backend (aceita absoluta/relativa/storage) usando host da API via env
const normalizeImageUrl = (raw) => {
  const str = (raw || "").trim();
  if (!str) return null;
  if (/^https?:\/\//i.test(str)) return str;
  const cleaned = str.replace(/^\/+/, "");
  const withoutStorage = cleaned.replace(/^storage\/+/, "");
  const apiBase =
    import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8001/api";
  const assetBase = String(apiBase).replace(/\/?api\/?$/, "");
  return `${assetBase}/storage/${withoutStorage}`;
};

// Verifica se a URL da imagem carrega corretamente
const verifyImageUrl = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(false);
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });

const pickFallback = () => placeholderPicture;

async function fetchRecentPosts() {
  try {
    const { data } = await getPosts(
      { perPage: 3 },
      {
        onUpdate: (fresh) => {
          const list = Array.isArray(fresh?.data) ? fresh.data : fresh || [];
          applyList(list);
        },
      },
    );
    const list = Array.isArray(data?.data) ? data.data : data || [];
    if (!list.length) {
      showSection.value = false;
      statePosts.value = [];
      return;
    }
    const sorted = list.slice().sort((a, b) => {
      const da = new Date(a.published_at || a.created_at || 0).getTime();
      const db = new Date(b.published_at || b.created_at || 0).getTime();
      return db - da;
    });
    const mappedRaw = sorted.map((p, idx) => {
      const normalized = p.image_url || normalizeImageUrl(p.image_path);
      return {
        id: p.id,
        slug: p.slug,
        tag:
          p && p.category && typeof p.category === "object"
            ? p.category?.name || p.category?.slug || "Geral"
            : p?.category && typeof p.category === "string" && p.category.trim()
              ? p.category.trim()
              : "Geral",
        title: p.title || "Sem título",
        alt: p.title || `Post ${idx + 1}`,
        imageMeta: { path: p.image_path || "", mime: p.image_mime || null },
        imageNormalized: normalized,
        image: null,
        imageConsistency: {
          originalPath: p.image_path || "",
          normalizedPath: normalized || "",
          verified: false,
          checked: false,
        },
        _idx: idx,
      };
    });

    // Verificar carregamento das imagens e garantir consistência
    const mapped = await Promise.all(
      mappedRaw.map(async (p) => {
        let finalUrl = p.imageNormalized;
        let verified = false;
        let usedFallback = false;
        if (!finalUrl) {
          console.warn(
            `RecentPosts: imagem ausente para post ${p.id || p.slug}. Caminho original vazio.`,
          );
          finalUrl = pickFallback();
          usedFallback = true;
        } else {
          verified = await verifyImageUrl(finalUrl);
          if (!verified) {
            console.warn(
              `RecentPosts: falha ao carregar imagem para post ${p.id || p.slug}: ${finalUrl}. Usando fallback.`,
            );
            finalUrl = pickFallback();
            usedFallback = true;
          }
        }
        const altText = usedFallback ? "Sem imagem" : p.alt || "Sem imagem";
        return {
          tag: p.tag,
          title: p.title,
          image: finalUrl,
          alt: altText,
          slug: p.slug,
          imageMeta: p.imageMeta,
          imageConsistency: {
            originalPath: p.imageMeta.path,
            normalizedPath: p.imageNormalized || "",
            verified,
            checked: true,
            usedFallback,
          },
        };
      }),
    );
    // Mostra só os posts reais retornados — sem preencher vagas com posts fictícios.
    const finalMapped = mapped.filter((p) => p);
    statePosts.value = finalMapped.slice(0, 3);
    showSection.value = finalMapped.length > 0;
  } catch (e) {
    // Falha ao buscar: sem posts reais para mostrar, a section fica oculta.
    console.warn("RecentPosts: falha ao buscar posts recentes.", e);
    statePosts.value = [];
    showSection.value = false;
  }
}

function applyList(list) {
  if (!Array.isArray(list) || !list.length) {
    showSection.value = false;
    statePosts.value = [];
    return;
  }
  const sorted = list.slice().sort((a, b) => {
    const da = new Date(a.published_at || a.created_at || 0).getTime();
    const db = new Date(b.published_at || b.created_at || 0).getTime();
    return db - da;
  });
  const mappedRaw = sorted.map((p, idx) => {
    const normalized = p.image_url || normalizeImageUrl(p.image_path);
    return {
      id: p.id,
      slug: p.slug,
      tag:
        p && p.category && typeof p.category === "object"
          ? p.category?.name || p.category?.slug || "Geral"
          : p?.category && typeof p.category === "string" && p.category.trim()
            ? p.category.trim()
            : "Geral",
      title: p.title || "Sem título",
      alt: p.title || `Post ${idx + 1}`,
      imageMeta: { path: p.image_path || "", mime: p.image_mime || null },
      imageNormalized: normalized,
      image: null,
      imageConsistency: {
        originalPath: p.image_path || "",
        normalizedPath: normalized || "",
        verified: false,
        checked: false,
      },
      _idx: idx,
    };
  });
  Promise.all(
    mappedRaw.map(async (p) => {
      let finalUrl = p.imageNormalized;
      let verified = false;
      let usedFallback = false;
      if (!finalUrl) {
        finalUrl = pickFallback();
        usedFallback = true;
      } else {
        verified = await verifyImageUrl(finalUrl);
        if (!verified) {
          finalUrl = pickFallback();
          usedFallback = true;
        }
      }
      const altText = usedFallback ? "Sem imagem" : p.alt || "Sem imagem";
      return {
        tag: p.tag,
        title: p.title,
        image: finalUrl,
        alt: altText,
        slug: p.slug,
        imageMeta: p.imageMeta,
        imageConsistency: {
          originalPath: p.imageMeta.path,
          normalizedPath: p.imageNormalized || "",
          verified,
          checked: true,
          usedFallback,
        },
      };
    }),
  )
    .then((mapped) => {
      statePosts.value = mapped.slice(0, 3);
      showSection.value = mapped.length > 0;
    })
    .catch(() => {
      statePosts.value = [];
      showSection.value = false;
    });
}

onMounted(fetchRecentPosts);

const mainPost = computed(() => statePosts.value[0] || {});
const sidePosts = computed(() => statePosts.value.slice(1, 3));
</script>
